/**
 * Coalesces high-frequency streaming updates into batched flushes.
 *
 * A streamed LLM turn delivers one delta per token; left unbatched, each delta
 * triggers a full re-render and an O(n) markdown re-parse, so cost grows
 * quadratically over the turn. A backgrounded tab throttles rendering, so the
 * work piles up and thrashes on refocus — the "switched away and it got slow"
 * symptom. This collapses many schedule() calls into one flush per frame while
 * visible and a slower cadence while hidden, flushing on refocus.
 */

export const VISIBLE_FLUSH_INTERVAL_MS = 16;

// Background tabs clamp timers to ~1s, so a sub-second target rounds up anyway.
export const HIDDEN_FLUSH_INTERVAL_MS = 750;

type Timers = {
    setTimeout: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>;
    clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
    isHidden: () => boolean;
};

const defaultTimers: Timers = {
    setTimeout: (cb, ms) => setTimeout(cb, ms),
    clearTimeout: (handle) => clearTimeout(handle),
    isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
};

// One shared listener drains all pending schedulers on refocus — a per-scheduler
// listener would leak one per Conversation, including short-lived nested agents.
const activeSchedulers = new Set<StreamingUpdateScheduler>();
let sharedVisibilityListenerAttached = false;

function ensureSharedVisibilityListener(): void {
    if (sharedVisibilityListenerAttached) return;
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
        return;
    }
    sharedVisibilityListenerAttached = true;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') return;
        // Copy first: flushing mutates the set (schedulers deregister themselves).
        for (const scheduler of Array.from(activeSchedulers)) {
            scheduler.flushNow(true);
        }
    });
}

export class StreamingUpdateScheduler {
    private readonly flush: () => void;
    private readonly timers: Timers;
    private pending = false;
    private handle: ReturnType<typeof setTimeout> | null = null;

    // timers is injectable for tests; defaults to real timers + visibilityState.
    constructor(flush: () => void, timers: Timers = defaultTimers) {
        this.flush = flush;
        this.timers = timers;
        ensureSharedVisibilityListener();
    }

    schedule(): void {
        this.pending = true;
        activeSchedulers.add(this);
        if (this.handle !== null) return;
        const interval = this.timers.isHidden()
            ? HIDDEN_FLUSH_INTERVAL_MS
            : VISIBLE_FLUSH_INTERVAL_MS;
        this.handle = this.timers.setTimeout(() => {
            this.handle = null;
            this.runFlush();
        }, interval);
    }

    // Flush now, cancelling any pending timer. Always fires by default (for
    // structural/terminal changes); onlyIfPending skips when nothing is buffered
    // (the refocus case).
    flushNow(onlyIfPending = false): void {
        if (onlyIfPending && !this.pending) return;
        if (this.handle !== null) {
            this.timers.clearTimeout(this.handle);
            this.handle = null;
        }
        this.runFlush(true);
    }

    private runFlush(force = false): void {
        if (!this.pending && !force) return;
        this.pending = false;
        activeSchedulers.delete(this);
        this.flush();
    }

    hasPending(): boolean {
        return this.pending;
    }

    dispose(): void {
        if (this.handle !== null) {
            this.timers.clearTimeout(this.handle);
            this.handle = null;
        }
        this.pending = false;
        activeSchedulers.delete(this);
    }
}
